/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { skipToken } from '@reduxjs/toolkit/query/react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { Link } from 'react-router-dom'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'
import { useDebounce } from 'use-debounce'

import DataTable from 'components/atoms/DataTable'
import Select from 'components/atoms/Select'
import { TableWrapper } from 'components/atoms/TableWrapper'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import routes from 'components/lab/constants/routes'
import ChargeRuleForm from 'components/lab/payer/chargeRules/ChargeRuleForm'
import {
  ChargeConfigFormValues,
  chargeRuleErrorMessage,
  CPT_CODE_OPTIONS,
  cptCodesForTestType,
  endsBeforeSuccessor,
  MAX_RATE_DAY,
  newFromConfig,
  StoredChargeConfig,
  TEST_TYPE_OPTIONS,
  toConfigFormValues,
  todayIso,
  toSaveRequest,
} from 'components/lab/payer/chargeRules/chargeRule'
import {
  ChargeConfigRow,
  chargeConfigColumns,
  ShowEndedCheckbox,
  useShowEnded,
} from 'components/lab/payer/chargeRules/chargeConfigTable'
import { PayerOption, payerSelectProps, toPayerOptions } from 'components/lab/payerChargeRules/payerFilter'
import { UserPermission } from 'components/lab/user/permissions'
import { useLabPermission } from 'hooks/useLabPermission'
import {
  useGetLabPayerQuery,
  useGetLabPayersQuery,
  useLazyGetLabPayerQuery,
} from 'store/services/payer'
import {
  useDeletePayerChargeRuleMutation,
  useGetPayerChargeRulesQuery,
  useSavePayerChargeRuleMutation,
} from 'store/services/payerChargeRules'

const StyledTableWrapper = styled(TableWrapper)`
  max-height: calc(100vh - 330px);
  td {
    vertical-align: middle;
  }
`

const DEFAULT_PAGE_SIZE = 20
const SEARCH_DELAY = 500

/**
 * A listing row. Spelled out here rather than imported from the backend DTO
 * because the shared DTO pulls in backend-only path aliases that this build
 * resolves to `any`.
 */
type ChargeRuleRow = ChargeConfigRow & {
  payerId: number
  payerName: string | null
  payerActive: boolean
  uPayerId?: string | null
}

const activeFilterOptions = [
  { key: 'true', value: 'Active payors' },
  { key: 'false', value: 'Inactive payors' },
]

/**
 * Every payer's charge rules in one searchable place, so a billing admin who
 * hits a "no charge rule configured" block can find the payer and add the rule
 * without going payer by payer. The same rules stay editable on the payer
 * screen's Charges tab; both write through the charge-rule command.
 */
const PayerChargeRules = () => {
  const canEdit = useLabPermission(UserPermission.EDIT_INSURANCE_PAYERS)

  // Inactive payors are the exception; showing them by default buries the rules
  // that are actually in use.
  const [filters, setFilters] = useState({ testType: '', cptCode: '', active: 'true' })
  // Blank by default: the list is then Active and Scheduled, plus Ended when
  // asked. A day lists what was in force then, scheduled rows excluded.
  const [inForceOn, setInForceOn] = useState('')
  const [showEnded, setShowEnded] = useShowEnded('lab.payerChargeRules.showEnded')
  const [payerFilter, setPayerFilter] = useState<PayerOption | null>(null)
  const [payerSearch, setPayerSearch] = useState('')
  const [debouncedPayerSearch] = useDebounce(payerSearch, SEARCH_DELAY)
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const [formValues, setFormValues] = useState<ChargeConfigFormValues>(toConfigFormValues)
  const [formPayer, setFormPayer] = useState<PayerOption | null>(null)
  const [showForm, setShowForm] = useState(false)

  const [savePayerChargeRule, { isLoading: isSaving }] = useSavePayerChargeRuleMutation()
  const [deletePayerChargeRule] = useDeletePayerChargeRuleMutation()

  // Payer pickers (filter and form) share one search: both need the same
  // name-to-id lookup and the endpoint already caps its result set.
  const { data: payerOptionsData } = useGetLabPayersQuery({
    name: debouncedPayerSearch,
    active: true,
  })

  const { data, isFetching } = useGetPayerChargeRulesQuery({
    ...(payerFilter ? { payerId: payerFilter.value } : {}),
    ...(filters.testType ? { testType: filters.testType } : {}),
    ...(filters.cptCode ? { cptCode: filters.cptCode } : {}),
    ...(filters.active ? { active: filters.active === 'true' } : {}),
    // A day lists what was in force then, ended or not; the toggle applies without one.
    ...(inForceOn ? { inForceOn } : { includeEnded: showEnded }),
    size: pageSize,
    page: currentPage,
  })

  useEffect(() => {
    setCurrentPage(0)
  }, [payerFilter, filters.testType, filters.cptCode, filters.active, inForceOn, showEnded])

  const cptFilterOptions = useMemo(() => {
    if (!filters.testType) {
      return CPT_CODE_OPTIONS
    }

    const allowed = cptCodesForTestType(filters.testType)

    return CPT_CODE_OPTIONS.filter((option) => allowed.includes(option.key))
  }, [filters.testType])

  const payerOptions = useMemo(() => toPayerOptions(payerOptionsData), [payerOptionsData])

  // The form checks a save against the payer's whole set, ended rows included,
  // not whatever the listing shows: a start already taken in the series may
  // sit on another page or be filtered out. The payer itself carries them all.
  const { data: formPayerData, isFetching: formPayerLoading } = useGetLabPayerQuery(
    formPayer ? { id: String(formPayer.value) } : skipToken,
  )
  const [fetchPayer] = useLazyGetLabPayerQuery()
  const configsOfFormPayer: StoredChargeConfig[] = useMemo(
    () => formPayerData?.charges ?? [],
    [formPayerData],
  )

  // The listing shows a row still billing the old rule's charge as "Not
  // recorded", the way reports read it; the payer's own row has the amount,
  // so an edit starts from it rather than asking for it again.
  const editedId = formValues.id
  useEffect(() => {
    const stored = editedId
      ? configsOfFormPayer.find((config) => config.id === editedId)
      : undefined

    if (stored?.billedFromRule && stored.billedAmount !== null) {
      const billedAmount = String(stored.billedAmount)

      setFormValues((p) =>
        p.id === stored.id && p.billedAmount.trim() === '' ? { ...p, billedAmount } : p,
      )
    }
  }, [configsOfFormPayer, editedId])

  // Terminated needs the row's successor, which only the listed rows can
  // show here; a successor on another page or filtered out goes unseen.
  const tableData = useMemo(() => {
    if (!data) {
      return data
    }

    const items = data.items as ChargeRuleRow[]

    return {
      ...data,
      items: items.map((row) => ({
        ...row,
        terminated:
          row.effectiveTo === row.endsOn &&
          endsBeforeSuccessor(
            items.filter((item) => item.payerId === row.payerId),
            row,
          ),
      })),
    }
  }, [data])

  const closeForm = () => {
    setShowForm(false)
    setFormValues(toConfigFormValues())
    setFormPayer(null)
  }

  const openForm = (values: ChargeConfigFormValues, payer: PayerOption | null) => {
    setFormValues(values)
    setFormPayer(payer)
    setShowForm(true)
  }

  const payerOf = (row: ChargeRuleRow): PayerOption => ({
    value: row.payerId,
    label: row.payerName ?? String(row.payerId),
  })

  const saveRule = async () => {
    if (!formPayer) {
      toast.error('Pick a payor for this charge')
      return
    }

    try {
      await savePayerChargeRule(toSaveRequest(formValues, formPayer.value)).unwrap()
      toast.success(`Charge saved for ${formPayer.label}`)

      if (inForceOn && formValues.effectiveFrom > inForceOn) {
        toast.info(
          `It starts on ${formValues.effectiveFrom}, after the "In force on" date, so the list hides it.`,
        )
      }
      closeForm()
    } catch (error) {
      toast.error(chargeRuleErrorMessage(error, 'The charge could not be saved.'))
    }
  }

  // The server refuses a delete that would leave an alternate in force with no
  // primary, and says why.
  const deleteRule = async (row: ChargeRuleRow) => {
    try {
      await deletePayerChargeRule({ payerId: row.payerId, id: row.id }).unwrap()
      toast.success('Charge deleted')
    } catch (error) {
      toast.error(chargeRuleErrorMessage(error, 'The charge could not be deleted.'))
    }
  }

  const tableColumns = [
    {
      accessor: 'payerName',
      Header: 'Payor',
      Cell: ({ row }: { row: { original: ChargeRuleRow } }) => (
        <div>
          <Link to={`${routes.index}${routes.payer.root}?payerId=${row.original.payerId}`}>
            {row.original.payerName ?? `#${row.original.payerId}`}
          </Link>
          <div css={{ fontSize: '0.8rem', color: '#6c757d' }}>
            #{row.original.payerId}
            {row.original.uPayerId ? ` · Payor ID ${row.original.uPayerId}` : ''}
            {row.original.payerActive ? '' : ' · inactive'}
          </div>
        </div>
      ),
    },
    ...chargeConfigColumns<ChargeRuleRow>({
      actions: {
        onEdit: (row) => openForm(toConfigFormValues(row), payerOf(row)),
        // A copy may go to another payor; the picker stays open for it.
        onNewFrom: (row) => openForm(newFromConfig(row, todayIso()), payerOf(row)),
        onDelete: deleteRule,
        configsOf: async (row) =>
          (await fetchPayer({ id: String(row.payerId) }).unwrap()).charges ?? [],
      },
    }),
  ]

  if (!canEdit) {
    return (
      <DashboardContent
        title="CHARGE RULES"
        icon="fas fa-file-invoice-dollar"
        content={
          <div className="card card-primary">
            <div className="card-body">
              <div className="alert alert-warning mb-0">
                You do not have permission to view charge rules. This screen needs the
                "Add/Update insurance payers" permission.
              </div>
            </div>
          </div>
        }
      />
    )
  }

  return (
    <DashboardContent
      title="CHARGE RULES"
      icon="fas fa-file-invoice-dollar"
      content={
        <div className="card card-primary">
          <div className="card-body">
            <div className="row align-items-end">
              <div className="col-lg-3 col-md-6">
                <div className="form-group mb-0">
                  <label className="text-gray" htmlFor="payerFilter">Payor</label>
                  <ReactSelect
                    inputId="payerFilter"
                    isClearable
                    placeholder="All payors"
                    value={payerFilter}
                    options={payerOptions}
                    onInputChange={(value) => setPayerSearch(value)}
                    onChange={(option) => setPayerFilter(option as PayerOption | null)}
                    {...payerSelectProps}
                  />
                </div>
              </div>
              <div className="col-lg-2 col-md-6">
                <Select
                  label="Test Type"
                  name="testType"
                  wrapperClassName="mb-0"
                  defaultLabel="All test types"
                  value={filters.testType}
                  handleChange={(event) =>
                    setFilters((p) => ({
                      ...p,
                      testType: event.target.value,
                      cptCode:
                        event.target.value
                        && !cptCodesForTestType(event.target.value).includes(p.cptCode)
                          ? ''
                          : p.cptCode,
                    }))
                  }
                  options={TEST_TYPE_OPTIONS}
                />
              </div>
              <div className="col-lg-2 col-md-6">
                <Select
                  label="CPT Code"
                  name="cptCode"
                  wrapperClassName="mb-0"
                  defaultLabel="All CPT codes"
                  value={filters.cptCode}
                  handleChange={(event) =>
                    setFilters((p) => ({ ...p, cptCode: event.target.value }))
                  }
                  options={cptFilterOptions}
                />
              </div>
              <div className="col-lg-2 col-md-6">
                <Select
                  label="Payor Status"
                  name="active"
                  wrapperClassName="mb-0"
                  defaultLabel="Any payor status"
                  value={filters.active}
                  handleChange={(event) => setFilters((p) => ({ ...p, active: event.target.value }))}
                  options={activeFilterOptions}
                />
              </div>
              <div className="col-lg-2 col-md-6">
                <div className="form-group mb-0">
                  <label className="text-gray" htmlFor="inForceOn">
                    In force on
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="inForceOn"
                    name="inForceOn"
                    value={inForceOn}
                    max={MAX_RATE_DAY}
                    onChange={(event) => setInForceOn(event.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end align-items-center mt-2">
              {inForceOn && (
                <span className="mr-auto" css={{ fontSize: '0.8rem', color: '#6c757d' }}>
                  Showing the charges in force on {inForceOn}. Clear the date to list every
                  day&rsquo;s.
                </span>
              )}
              <ShowEndedCheckbox
                checked={showEnded}
                onChange={setShowEnded}
                disabled={Boolean(inForceOn)}
                title={inForceOn ? 'Clear "In force on" to list ended charges' : undefined}
              />
              <Button
                variant="primary"
                className="ml-3"
                onClick={() => openForm(toConfigFormValues(), payerFilter)}
                disabled={showForm}>
                Add Charge Rule
              </Button>
            </div>

            <Modal size="xl" show={showForm} onHide={closeForm}>
              <Modal.Body className="p-0">
                <ChargeRuleForm
                  values={formValues}
                  setValues={setFormValues}
                  configs={configsOfFormPayer}
                  onSave={saveRule}
                  onCancel={closeForm}
                  saving={isSaving}
                  disabled={!formPayer}
                  loading={formPayerLoading}
                  className="mb-0 border-0">
                  <div className="col-lg-4 col-md-6 mb-2">
                    <label className="text-gray" htmlFor="formPayer">
                      Payor
                    </label>
                    <ReactSelect
                      inputId="formPayer"
                      placeholder="Select payor..."
                      // Moving a saved configuration to another payer would be a
                      // delete plus an add, not an edit, so the payer is fixed
                      // while editing.
                      isDisabled={Boolean(formValues.id)}
                      value={formPayer}
                      options={payerOptions}
                      onInputChange={(value) => setPayerSearch(value)}
                      onChange={(option) => setFormPayer(option as PayerOption | null)}
                      {...payerSelectProps}
                    />
                  </div>
                  {!formPayer && (
                    <div className="col-12">
                      <div className="alert alert-info py-2">Pick a payor to save this charge.</div>
                    </div>
                  )}
                </ChargeRuleForm>
              </Modal.Body>
            </Modal>

            <StyledTableWrapper className="row">
              <DataTable
                tableKey="lab_payer_charge_rules"
                data={tableData}
                status={isFetching}
                tableColumns={tableColumns}
                pageSize={pageSize}
                setPageSize={setPageSize}
                setCurrentPage={setCurrentPage}
                setSortOptions={() => {}}
                entityName="charge rules"
              />
            </StyledTableWrapper>
          </div>
        </div>
      }
      breadcrumb={[
        {
          label: 'Charge Rules',
          to: `${routes.index}${routes.payerChargeRules.root}`,
        },
      ]}
    />
  )
}

export default PayerChargeRules
